import React, { useRef, useState } from "react";
import { apiClient } from "../lib/api";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Badge } from "./ui/badge";
import { Upload, FileText, AlertCircle, CheckCircle2, Loader2, XCircle } from "lucide-react";
import { toast } from "sonner";

const EXPECTED_HEADER = "Ticker;Data;Abertura;Maxima;Minima;Fechamento;Ajustado;Volume";
const EXPECTED_COLS = EXPECTED_HEADER.split(";");
const MAX_SIZE_MB = 50;

async function clientValidate(file) {
  /**
   * Quick client-side checks BEFORE upload:
   * - file size
   * - header matches
   * - first non-header row has 8 fields and a YYYY-MM-DD date in column 2
   */
  if (file.size === 0) return { ok: false, error: "Arquivo vazio." };
  if (file.size > MAX_SIZE_MB * 1024 * 1024) {
    return { ok: false, error: `Arquivo maior que ${MAX_SIZE_MB} MB.` };
  }
  // Read first ~16 KB to inspect header + sample
  const sample = await file.slice(0, 16 * 1024).text();
  // Strip BOM if present
  const cleaned = sample.replace(/^\uFEFF/, "");
  const lines = cleaned.split(/\r?\n/).filter((l) => l.trim() !== "");
  if (lines.length === 0) return { ok: false, error: "Arquivo vazio ou sem linhas." };

  const headerLine = lines[0].trim();
  if (headerLine !== EXPECTED_HEADER) {
    return {
      ok: false,
      error: `Cabeçalho inválido. Esperado: "${EXPECTED_HEADER}". Recebido: "${headerLine}".`,
    };
  }
  if (lines.length < 2) {
    return { ok: false, error: "CSV sem linhas de dados." };
  }
  // Spot check the first data row
  const firstRow = lines[1].split(";");
  if (firstRow.length !== EXPECTED_COLS.length) {
    return {
      ok: false,
      error: `Primeira linha tem ${firstRow.length} colunas, esperado ${EXPECTED_COLS.length}.`,
    };
  }
  const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
  if (!dateRegex.test(firstRow[1].trim())) {
    return {
      ok: false,
      error: `Coluna "Data" da primeira linha não está em YYYY-MM-DD. Recebido: "${firstRow[1]}".`,
    };
  }
  // Numeric fields
  const numericIdx = [2, 3, 4, 5, 6, 7];
  for (const idx of numericIdx) {
    if (firstRow[idx] && isNaN(Number(firstRow[idx].replace(",", ".")))) {
      return {
        ok: false,
        error: `Coluna "${EXPECTED_COLS[idx]}" da primeira linha não é numérica: "${firstRow[idx]}".`,
      };
    }
  }
  return {
    ok: true,
    sampleRows: lines.length - 1,
    firstDate: firstRow[1].trim(),
    firstTicker: firstRow[0].trim(),
  };
}

export default function CsvUploader({ onIngested }) {
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [clientCheck, setClientCheck] = useState(null);
  const [serverCheck, setServerCheck] = useState(null);
  const [validating, setValidating] = useState(false);
  const [ingesting, setIngesting] = useState(false);

  const reset = () => {
    setFile(null);
    setClientCheck(null);
    setServerCheck(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  const handleFileChange = async (e) => {
    const f = e.target.files?.[0];
    setServerCheck(null);
    setClientCheck(null);
    if (!f) {
      setFile(null);
      return;
    }
    setFile(f);
    const check = await clientValidate(f);
    setClientCheck(check);
    if (check.ok) {
      // Run server-side validation for full preview
      setValidating(true);
      try {
        const fd = new FormData();
        fd.append("file", f);
        const r = await apiClient.post("/admin/data/validate-csv", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setServerCheck({ ok: true, ...r.data });
      } catch (err) {
        setServerCheck({ ok: false, error: err?.response?.data?.detail || "Falha ao validar." });
      } finally {
        setValidating(false);
      }
    }
  };

  const handleIngest = async () => {
    if (!file) return;
    if (!window.confirm(
      `Confirma a carga deste CSV? Cotações existentes para os tickers do arquivo serão substituídas.`,
    )) return;
    setIngesting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("source", "real-csv");
      const r = await apiClient.post("/admin/data/ingest-csv", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 5 * 60 * 1000,  // up to 5 min for large files
      });
      toast.success(
        `CSV ingerido: ${r.data.ingest.rows_inserted} cotações em ${r.data.ingest.matched_tickers} tickers (substituídas ${r.data.ingest.wiped_existing} anteriores).`,
      );
      reset();
      onIngested && onIngested(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha na ingestão.");
    } finally {
      setIngesting(false);
    }
  };

  const canIngest =
    file && clientCheck?.ok && serverCheck?.ok && !validating && !ingesting;

  return (
    <Card data-testid="csv-uploader-card">
      <CardHeader>
        <CardTitle className="font-display text-lg">Carregar dados via CSV</CardTitle>
        <CardDescription>
          Formato esperado (UTF-8, separador `;`):
          <code className="block mt-2 text-[11px] font-mono-tab bg-muted px-2 py-1 rounded">
            {EXPECTED_HEADER}
          </code>
          Tickers da B3 (até {MAX_SIZE_MB} MB). Cotações sintéticas ou anteriores
          para os tickers do arquivo serão substituídas.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          <input
            ref={inputRef}
            type="file"
            accept=".csv,text/csv"
            onChange={handleFileChange}
            className="hidden"
            data-testid="csv-uploader-input"
          />
          <Button
            variant="outline"
            onClick={() => inputRef.current?.click()}
            disabled={ingesting}
            data-testid="csv-uploader-pick"
          >
            <Upload className="w-4 h-4 mr-2" />
            Selecionar CSV
          </Button>
          {file && (
            <div className="flex items-center gap-2 text-sm">
              <FileText className="w-4 h-4 text-muted-foreground" />
              <span data-testid="csv-uploader-filename">{file.name}</span>
              <Badge variant="outline" className="text-[10px]">
                {(file.size / 1024 / 1024).toFixed(2)} MB
              </Badge>
              <button
                onClick={reset}
                className="text-xs text-muted-foreground hover:text-foreground"
                data-testid="csv-uploader-clear"
              >
                <XCircle className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
        </div>

        {clientCheck && !clientCheck.ok && (
          <div
            className="flex items-start gap-2 text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-md px-3 py-2"
            data-testid="csv-uploader-error-client"
          >
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
            <div>
              <div className="font-semibold">Validação inicial falhou</div>
              <div className="text-xs mt-0.5">{clientCheck.error}</div>
            </div>
          </div>
        )}

        {clientCheck?.ok && validating && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="csv-uploader-validating">
            <Loader2 className="w-4 h-4 animate-spin" />
            Validando arquivo no servidor…
          </div>
        )}

        {serverCheck && !serverCheck.ok && (
          <div
            className="flex items-start gap-2 text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-md px-3 py-2"
            data-testid="csv-uploader-error-server"
          >
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
            <div>
              <div className="font-semibold">Validação do servidor falhou</div>
              <div className="text-xs mt-0.5">{serverCheck.error}</div>
            </div>
          </div>
        )}

        {serverCheck?.ok && (
          <div
            className="border border-buy bg-buy-soft rounded-md p-3 text-sm"
            data-testid="csv-uploader-preview"
          >
            <div className="flex items-center gap-2 font-semibold text-buy-strong">
              <CheckCircle2 className="w-4 h-4" /> Arquivo válido
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3 text-xs">
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Linhas</div>
                <div className="font-mono-tab font-semibold">{serverCheck.rows.toLocaleString("pt-BR")}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Tickers</div>
                <div className="font-mono-tab font-semibold">{serverCheck.tickers}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Início</div>
                <div className="font-mono-tab font-semibold">{serverCheck.date_min}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Fim</div>
                <div className="font-mono-tab font-semibold">{serverCheck.date_max}</div>
              </div>
            </div>
            {serverCheck.sample_tickers?.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-1">
                {serverCheck.sample_tickers.map((t) => (
                  <Badge key={t} variant="outline" className="text-[10px]">{t}</Badge>
                ))}
                {serverCheck.tickers > serverCheck.sample_tickers.length && (
                  <Badge variant="secondary" className="text-[10px]">
                    +{serverCheck.tickers - serverCheck.sample_tickers.length} outros
                  </Badge>
                )}
              </div>
            )}
          </div>
        )}

        <div>
          <Button
            onClick={handleIngest}
            disabled={!canIngest}
            data-testid="csv-uploader-ingest"
          >
            {ingesting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Carregando…
              </>
            ) : (
              <>
                <Upload className="w-4 h-4 mr-2" />
                Carregar agora
              </>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
